/**
 * Wraps react-final-form's `handleSubmit` so that, when sync validation
 * fails, the page scrolls to and focuses the first invalid field by `id`.
 *
 * Why a wrapper instead of a side-effect component:
 * final-form only notifies subscribers when formState actually changes.
 * On the 2nd failed submit with the same errors, `submitFailed` is
 * already `true` and every field is already `touched`, so nothing
 * changes and no useEffect can fire. Wrapping the submit handler
 * guarantees we run on every click.
 *
 * Field <InputField>s must pass `id={input.name}` so the focusable
 * element actually receives that DOM id.
 *
 * Usage inside a react-final-form <Form> render prop:
 *   {({ handleSubmit, form }) => (
 *     <form onSubmit={scrollToFirstErrorOnSubmit(handleSubmit, form)}>
 *       ...
 *     </form>
 *   )}
 */
export default function scrollToFirstErrorOnSubmit(handleSubmit, form) {
  return (event) => {
    const result = handleSubmit(event);
    // form.submit() ran synchronously above. Inspect state right after.
    const { submitFailed, hasValidationErrors, errors } = form.getState();
    if (submitFailed && hasValidationErrors && errors) {
      const firstKey = Object.keys(errors)[0];
      if (firstKey) {
        const el = document.getElementById(firstKey);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof el.focus === "function") {
            try {
              el.focus({ preventScroll: true });
            } catch {
              el.focus();
            }
          }
        }
      }
    }
    return result;
  };
}
